import z from 'zod';

import {
    BatteryTechnologies,
    Compliance,
    Compliances,
    PortPurpose,
    RegionAvailability,
    SolarTechnologies,
} from '../validators';
import { ComponentApplication } from '../validators/Component';
import { CableLinesValidator } from '../validators/components/Cable';
import { ACPortConfiguration } from '../validators/fields/AbstractPort.ACPortConfiguration';
import { AllPortControlMethods } from '../validators/fields/AbstractPort.ControlMethods';
import { BreakerTypes } from '../validators/fields/BreakerType';
import { ChargerConnectorType } from '../validators/fields/ChargerConnector';
import { ComponentType } from '../validators/fields/ComponentType';
import { EnergyCapacity } from '../validators/fields/EnergyCapacity';
import { openingTime as openingTimeValues } from '../validators/fields/OpeningTime';
import { PowerFlowDirection } from '../validators/fields/PowerFlowDirection';
import { WireSize } from '../validators/fields/WireSize';
import { Component } from './Component';
import { ConverterCategory } from './ConverterCategory';

const textSearchQuerySchema = z.object({
    search: z.string().optional(),
});

const manufacturerFilterSchema = z.union([z.string(), z.array(z.string()).min(1)]);

const portQuerySchema = z.object({
    voltageType: z.enum(['AC', 'DC']).optional(),
    voltage: z
        .object({
            min: z.number().optional(),
            nom: z.number().optional(),
            max: z.number().optional(),
        })
        .optional(),
    power: z
        .object({
            min: z.number().optional(),
            nom: z.number().optional(),
            max: z.number().optional(),
        })
        .optional(),
    current: z
        .object({
            min: z.number().optional(),
            nom: z.number().optional(),
            max: z.number().optional(),
        })
        .optional(),
    powerFlowDirection: PowerFlowDirection.validator.optional(),
    strictDirection: z.boolean().optional(),
    // Only used within `excludePorts`: keep the component only if it has at most `maxCount` ports
    // matching this pattern (defaults to 0, i.e. none allowed).
    maxCount: z.number().optional(),
    // This query may be satisfied by a port already matched by another port query
    allowSharedMatch: z.boolean().optional(),
    isolated: z.boolean().optional(),
    purpose: PortPurpose.validator.optional(),
    controlMethods: z.array(z.enum(AllPortControlMethods)).optional(),
    acConfiguration: z.array(ACPortConfiguration.validator.removeDefault().unwrap()).optional(),
});

export enum SortType {
    RELEVANCE = 'relevance',
    TYPE = 'type',
    MANUFACTURER = 'manufacturer',
    MANUFACTURER_DESC = 'manufacturerDesc',
    NAME = 'name',
    CREATED_AT = 'createdAt',
    POWER_LEVEL_ASC = 'powerLevelAsc',
    POWER_LEVEL_DESC = 'powerLevelDesc',
    VOLTAGE_ASC = 'voltageAsc',
    VOLTAGE_DESC = 'voltageDesc',
    CAPACITY_ASC = 'capacityAsc',
    CAPACITY_DESC = 'capacityDesc',
    INPUT_VOLTAGE_ASC = 'inputVoltageAsc',
    INPUT_VOLTAGE_DESC = 'inputVoltageDesc',
    OUTPUT_VOLTAGE_ASC = 'outputVoltageAsc',
    OUTPUT_VOLTAGE_DESC = 'outputVoltageDesc',
    OUTPUT_CAPACITY_ASC = 'outputCapacityAsc',
    OUTPUT_CAPACITY_DESC = 'outputCapacityDesc',
    RATED_CURRENT_ASC = 'ratedCurrentAsc',
    RATED_CURRENT_DESC = 'ratedCurrentDesc',
    BREAKING_CAPACITY_ASC = 'breakingCapacityAsc',
    BREAKING_CAPACITY_DESC = 'breakingCapacityDesc',
}

export enum ComponentSearchScore {
    PREMIUM_MANUFACTURER = 'premium_manufacturer',
    PORT_MATCH_SCORE = 'port_match_score',
    ENERGY_CAPACITY_SCORE = 'energy_capacity_score',
    RATING_CLOSENESS = 'rating_closeness',
    COMPATIBLE_PRODUCT = 'compatible_product',
    TEXT_SCORE = 'text_score',
    VECTOR_SCORE = 'vector_score',
    SORT_RANK = 'sort_rank',
    COMPLETENESS = 'completeness',
    PRIORITY_MANUFACTURER = 'priority_manufacturer',
    MANUFACTURER_FOR_ACTIVE_EVENTS = 'manufacturer_for_active_events',
    DC_CATALOG = 'dc_catalog',
    HAS_PRICING = 'has_pricing',
}

export const catalogSchema = z.enum(['dc', 'odca', 'currentos']);
export type Catalog = z.infer<typeof catalogSchema>;

export const membershipCatalogSchema = z.enum(['odca', 'currentos']);
export type MembershipCatalog = z.infer<typeof membershipCatalogSchema>;

export const CATALOG_MEMBERSHIP = {
    odca: { name: 'ODCA', compliance: Compliance.ODCA },
    currentos: { name: 'CurrentOS', compliance: Compliance.CURRENT_OS },
} as const satisfies Record<MembershipCatalog, { name: string; compliance: Compliance }>;

const ratingRangeSchema = z.object({ min: z.number().optional(), max: z.number().optional() });

export const componentQuerySchema = z
    .object({
        inAppSupport: z.boolean().optional(),
        query: z.string().optional(),
        type: ComponentType.validator.optional(),
        catalog: catalogSchema.optional(),
        featuredSubcategory: z.union([ComponentType.validator, z.nativeEnum(ConverterCategory)]).optional(),
        manufacturer: manufacturerFilterSchema.optional(),
        distributor: z.string().optional(),
        application: z.nativeEnum(ComponentApplication).array().optional(),
        ports: z.array(portQuerySchema).optional(),
        // Forbidden port patterns: a component is excluded if it has any port matching any of
        // these. Used to enforce exclusivity rules (e.g. a rectifier has no other AC/input ports).
        excludePorts: z.array(portQuerySchema).optional(),
        maxPorts: z.number().optional(),
        regionAvailability: RegionAvailability.validator.optional(),
        compliances: Compliances.validator.optional(),
        canServeAs: z.string().array().optional(),
        charging: z
            .object({
                connectors: z.array(z.nativeEnum(ChargerConnectorType)).optional(),
                power: z.object({ value: z.number() }).optional(),
                numberOfConnectors: z.number().optional(),
            })

            .optional(),
        includeArchived: z.boolean().default(false).optional(),
        energyCapacity: z.object({ value: z.number().optional(), unit: z.enum(EnergyCapacity.units) }).optional(),
        ratings: z
            .object({
                power: ratingRangeSchema.optional(),
                voltage: ratingRangeSchema.optional(),
                current: ratingRangeSchema.optional(),
            })
            .optional(),
        batteryTechnologies: z.array(BatteryTechnologies.validator.element).optional(),
        solarTechnologies: z.array(SolarTechnologies.validator.element).optional(),
        breakerType: z.array(z.nativeEnum(BreakerTypes)).optional(),
        openingTime: z.array(z.enum(openingTimeValues)).optional(),
        fusePoles: z.number().optional(),
        fuseMinBreakingCapacity: z.number().optional(),
        breakerMinBreakingCapacity: z.number().optional(),
        aiEnabled: z.boolean().default(false).optional(),
        priorityManufacturer: z.string().optional(),
        productsForCompatibility: z.array(z.string()).optional(),
        maxPrice: z.number().optional(),
        hasPrice: z.boolean().optional(),
        hasLeadTime: z.boolean().optional(),
        hasCompatibleWith: z.boolean().optional(),
        event: z.string().optional(),
        sort: z
            .enum([
                SortType.RELEVANCE,
                SortType.MANUFACTURER,
                SortType.MANUFACTURER_DESC,
                SortType.TYPE,
                SortType.NAME,
                SortType.CREATED_AT,
                SortType.POWER_LEVEL_ASC,
                SortType.POWER_LEVEL_DESC,
                SortType.VOLTAGE_ASC,
                SortType.VOLTAGE_DESC,
                SortType.CAPACITY_ASC,
                SortType.CAPACITY_DESC,
                SortType.INPUT_VOLTAGE_ASC,
                SortType.INPUT_VOLTAGE_DESC,
                SortType.OUTPUT_VOLTAGE_ASC,
                SortType.OUTPUT_VOLTAGE_DESC,
                SortType.OUTPUT_CAPACITY_ASC,
                SortType.OUTPUT_CAPACITY_DESC,
                SortType.RATED_CURRENT_ASC,
                SortType.RATED_CURRENT_DESC,
                SortType.BREAKING_CAPACITY_ASC,
                SortType.BREAKING_CAPACITY_DESC,
            ])
            .optional(),
        cable: z
            .object({
                wireSize: WireSize.validator.optional(),
                voltage: z
                    .object({
                        max: z.number().optional(),
                    })
                    .optional(),
                voltageType: z.enum(['AC', 'DC']).default('DC').optional(),
                lines: CableLinesValidator.default({}).optional(),
            })
            .optional(),
    })
    .merge(textSearchQuerySchema);

export enum ProfileSearchScore {
    TEXT_SCORE = 'text_score',
    VECTOR_SCORE = 'vector_score',
    COMPLETENESS_SCORE = 'completeness_score',
    PREMIUM_MANUFACTURER = 'premium_manufacturer',
    HAS_PRODUCTS = 'has_products',
    PRODUCT_COUNT = 'product_count',
}

const profileLocationSchema = z
    .object({
        label: z.string().nullable().optional(),
        coordinates: z.object({ longitude: z.number(), latitude: z.number() }),
    })
    .nullable()
    .optional();

export const customWeightsSchema = z.record(z.string(), z.number().finite());

export const profileQuerySchema = z
    .object({
        inAppSupport: z.boolean().optional(),
        services: z.array(z.string()).optional(),
        applicationTags: z.array(z.string()).optional(),
        status: z.string().optional(),
        isRePlus: z.boolean().optional(),
        compliances: Compliances.validator.optional(),
        event: z.string().optional(),
        hall: z.string().optional(),
        manufacturer: manufacturerFilterSchema.optional(),
        systemSize: z.number().optional(),
        projectBudget: z.number().optional(),
        location: profileLocationSchema,
        industrySubcategories: z.array(z.string()).optional(),
        catalog: catalogSchema.optional(),
        withCatalogProductsOnly: z.boolean().optional(),
        productType: ComponentType.validator.optional(),
        promoteManufacturers: z.boolean().optional(),
    })
    .merge(textSearchQuerySchema);

export const referenceDesignQuerySchema = z
    .object({
        inAppSupport: z.boolean().optional(),
        storage: z.number().optional(),
        grid: z.number().optional(),
        generation: z.number().optional(),
        load: z.number().optional(),
        manufacturer: manufacturerFilterSchema.optional(),
        tags: z.string().array().default([]).optional(),
    })
    .merge(textSearchQuerySchema);

export const caseStudySchema = z
    .object({
        manufacturer: manufacturerFilterSchema.optional(),
    })
    .merge(textSearchQuerySchema);

export enum ArticleSearchScore {
    PREMIUM_MANUFACTURER = 'premium_manufacturer',
    TEXT_SCORE = 'text_score',
    VECTOR_SCORE = 'vector_score',
}

export const articleSearchQuerySchema = z
    .object({
        manufacturer: manufacturerFilterSchema.optional(),
    })
    .merge(textSearchQuerySchema);

export type ComponentQuery = z.infer<typeof componentQuerySchema>;
export type ProfileQuery = z.infer<typeof profileQuerySchema>;
export type ReferenceDesignQuery = z.infer<typeof referenceDesignQuerySchema>;
export type CaseStudyQuery = z.infer<typeof caseStudySchema>;
export type PortQuery = z.infer<typeof portQuerySchema>;
export type Query = ComponentQuery | ProfileQuery;
export type TextSearchQuery = z.infer<typeof textSearchQuerySchema>;
export type ArticleQuery = z.infer<typeof articleSearchQuerySchema>;

export type GeneratedSearchFilters = {
    productFilters?: ComponentQuery;
    profileFilters?: ProfileQuery;
    designFilters?: ReferenceDesignQuery;
    caseStudyFilters?: CaseStudyQuery;
};

export type ComponentSearchManufacturerFacet = {
    id: string;
    name: string;
    count: number;
    logos: { small?: string | null; large?: string | null } | null;
};

export type ComponentSearchFacets = {
    manufacturers?: ComponentSearchManufacturerFacet[];
    breakerTypes?: Record<string, number>;
    openingTimes?: Record<string, number>;
    batteryTechnologies?: Record<string, number>;
    solarTechnologies?: Record<string, number>;
    regions?: Record<string, number>;
    compliances?: Record<string, number>;
    compatibleWith?: number;
};

export type ComponentFacetKey = keyof ComponentSearchFacets;

export const componentFacetsSchema = z.array(
    z.enum([
        'manufacturers',
        'breakerTypes',
        'openingTimes',
        'batteryTechnologies',
        'solarTechnologies',
        'regions',
        'compliances',
        'compatibleWith',
    ]),
);

// The AI Type Contortionist Presents:
// Fails to compile if a facet is added to ComponentSearchFacets but left out of
// the schema above, which would quietly make it impossible to ask for.
type UnrequestableFacet = Exclude<ComponentFacetKey, z.infer<typeof componentFacetsSchema>[number]>;
const _allFacetsRequestable: UnrequestableFacet extends never ? true : never = true;
void _allFacetsRequestable;

export type FilterSuggestion = { filter: Partial<ComponentQuery>; count: number };

export type PickedSuggestion = FilterSuggestion & { position: number };

export type NamedMaker = { name: string; slug: string };

export type ComponentSearchData = {
    hasSearched: boolean;
    docs: Component[];
    page: number;
    totalPages: number;
    totalComponents: number;
    isLoading: boolean;
    error: boolean;
    facets?: ComponentSearchFacets;
    suggestions?: FilterSuggestion[];
    request?: { query: string; filters: ComponentQuery; suggestion?: PickedSuggestion };
    exact: boolean;
    makers?: NamedMaker[];
};

export enum ViewOnlyFilter {
    TYPE = 'type',
    MANUFACTURER = 'manufacturer',
    DISTRIBUTOR = 'distributor',
    PRODUCTS_FOR_COMPATIBILITY = 'productsForCompatibility',
}
